import React, { useState, useEffect } from 'react';
import { MetricCard, Card, CardTitle, BarRow, Grid, Insight } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
function fmt(n) { return n === null || n === undefined ? '\u2026' : n.toLocaleString(); }
function pct(a, b) { return b ? (a / b * 100).toFixed(1) + '%' : '0%'; }

function Skel({ h = 12, mb = 10 }) {
  return (
    <div style={{ height: h, borderRadius: 6, marginBottom: mb,
      background: 'linear-gradient(90deg,#edf2f5 25%,#f5f7f9 50%,#edf2f5 75%)',
      backgroundSize: '200% 100%', animation: 'shimmer 1.6s ease infinite' }} />
  );
}

function FreshnessBar({ label, count, total, color }) {
  const p = total ? Math.round((count / total) * 100) : 0;
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: '.72rem', color: '#4a6878' }}>{label}</span>
        <span style={{ fontSize: '.72rem', fontWeight: 600, color: '#0f1e28' }}>
          {fmt(count)} <span style={{ color: '#9eb8c8', fontWeight: 400 }}>({p}%)</span>
        </span>
      </div>
      <div style={{ height: 7, borderRadius: 4, background: '#eef2f5', overflow: 'hidden' }}>
        <div style={{ width: p + '%', height: '100%', background: color, borderRadius: 4,
          transition: 'width .8s ease' }} />
      </div>
    </div>
  );
}

export default function Data() {
  const [stats,        setStats]        = useState(null);
  const [countryStats, setCountryStats] = useState([]);
  const [countryCount, setCountryCount] = useState(null);
  const [gpsStats,     setGpsStats]     = useState(null);
  const [pipelineRuns, setPipelineRuns] = useState([]);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Use get_admin_stats RPC for freshness (single round-trip instead of 4 HEAD queries)
        const { data: adminStats } = await supabase.rpc('get_admin_stats');
        let total = 0, fresh90 = 0, stale = 0, never = 0, mid = 0;
        if (adminStats && adminStats.total) {
          total  = adminStats.total  || 0;
          fresh90= adminStats.fresh  || 0;
          stale  = adminStats.stale  || 0;
          never  = adminStats.never  || 0;
          mid    = Math.max(total - fresh90 - stale - never, 0);
        }

        // GPS count — single query (not parallel HEAD)
        const { count: gpsWithCoords } = await supabase
          .from('Locations').select('*', { count: 'exact', head: true })
          .not('latitude', 'is', null).neq('latitude', 0);

        if (!cancelled) {
          setStats({ total, fresh90, mid, stale, never });
          setGpsStats({ total, withCoords: gpsWithCoords || 0 });
        }

        // Coverage per country from RPC
        const { data: cov } = await supabase.rpc('get_coverage_stats');
        if (!cancelled && cov) {
          const byCountry = cov.freshnessbyCountry || [];
          setCountryStats(byCountry
            .sort((a, b) => (b.fresh || 0) - (a.fresh || 0))
            .slice(0, 8)
          );
          setCountryCount(cov.totalCountries || cov.countryCount || byCountry.length || null);
        }

        // Recent pipeline runs
        const { data: runs } = await supabase
          .from('pipeline_runs')
          .select('pipeline,country,status,started_at,finished_at,total,processed,succeeded,failed')
          .order('started_at', { ascending: false })
          .limit(5);
        if (!cancelled) setPipelineRuns(runs || []);

      } catch (_e) { console.error('Data stats load error:', _e); }
      if (!cancelled) setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const total     = stats?.total || 0;
  const fresh90   = stats?.fresh90 || 0;
  const mid       = stats?.mid || 0;
  const stale     = stats?.stale || 0;
  const never     = stats?.never || 0;
  const freshPct  = total ? Math.round((fresh90 / total) * 100) : 0;

  const gpsTotal  = gpsStats?.total || total;
  const gpsCoords = gpsStats?.withCoords || 0;
  const gpsPct    = gpsTotal ? Math.round((gpsCoords / gpsTotal) * 100) : 0;

  return (
    <div style={{ fontFamily: F }}>
      <style>{'@keyframes shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}'}</style>

      <Grid cols={4}>
        <MetricCard
          label="Total locations"
          value={loading ? '\u2026' : fmt(total)}
          sub={!loading ? (countryCount || 36) + ' countries' : undefined}
          accent="#314F5C"
        />
        <MetricCard
          label="Fresh (< 90 days)"
          value={loading ? '\u2026' : freshPct + '%'}
          sub={!loading ? fmt(fresh90) + ' locations' : undefined}
          trend={freshPct > 50 ? 'up' : 'down'}
          accent={freshPct > 50 ? '#2da06a' : '#d97706'}
        />
        <MetricCard
          label="GPS coverage"
          value={loading ? '\u2026' : gpsPct + '%'}
          sub={!loading ? fmt(gpsCoords) + ' with coordinates' : undefined}
          trend={gpsPct > 80 ? 'up' : 'down'}
          accent={gpsPct > 80 ? '#2da06a' : '#d97706'}
        />
        <MetricCard
          label="Never verified"
          value={loading ? '\u2026' : fmt(never)}
          sub={!loading && total ? pct(never, total) + ' of database' : undefined}
          trend={never > total * 0.3 ? 'down' : null}
          accent={never > total * 0.3 ? '#e05c5c' : '#9eb8c8'}
        />
      </Grid>

      <Grid cols={2} mb={0}>

        <Card style={{ marginBottom: 0 }}>
          <CardTitle>Freshness breakdown</CardTitle>
          {loading ? [1,2,3,4].map(i => <Skel key={i} h={38} mb={14} />) : (
            <>
              <FreshnessBar label="Fresh < 90 days"   count={fresh90} total={total} color="#2da06a" />
              <FreshnessBar label="Moderate 90–365d"  count={mid}     total={total} color="#d97706" />
              <FreshnessBar label="Stale > 1 year"    count={stale}   total={total} color="#e05c5c" />
              <FreshnessBar label="Never verified"    count={never}   total={total} color="#b0c8d4" />
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: '.5px solid rgba(49,79,92,.06)' }}>
                <FreshnessBar label="GPS coordinates" count={gpsCoords} total={gpsTotal} color="#314F5C" />
              </div>
            </>
          )}
        </Card>

        <Card style={{ marginBottom: 0 }}>
          <CardTitle>Freshest countries</CardTitle>
          {loading ? [1,2,3,4,5,6,7,8].map(i => <Skel key={i} mb={8} />) : (
            countryStats.length === 0
              ? <Insight type="info">No country-level freshness data yet.</Insight>
              : countryStats.map(row => {
                  const total_c = (row.fresh || 0) + (row.stale || 0) + (row.never || 0);
                  const freshP  = total_c ? Math.round(((row.fresh || 0) / total_c) * 100) : 0;
                  return (
                    <BarRow key={row.country} label={row.country}
                      value={freshP + '%'} pct={freshP} labelWidth={120}
                      color={freshP > 70 ? '#2da06a' : freshP > 40 ? '#d97706' : '#e05c5c'} />
                  );
                })
          )}
        </Card>
      </Grid>

      {pipelineRuns.length > 0 && (
        <Card style={{ marginTop: 14 }}>
          <CardTitle>Recent pipeline runs</CardTitle>
          <div style={{ overflowX: 'auto' }}><div style={{ minWidth: 520 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
            gap: 8, padding: '6px 0', borderBottom: '1px solid #e9eef2',
            fontSize: '.6rem', fontWeight: 600, color: '#9eb8c8',
            textTransform: 'uppercase', letterSpacing: '.06em' }}>
            <div>Pipeline</div><div>Country</div><div>Status</div>
            <div>Processed</div><div>Started</div>
          </div>
          {pipelineRuns.map((run, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 1fr 1fr 1fr',
              gap: 8, padding: '8px 0', borderBottom: '.5px solid rgba(49,79,92,.04)',
              fontSize: '.7rem', alignItems: 'center' }}>
              <div style={{ fontWeight: 500, color: '#0f1e28', overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{run.pipeline}</div>
              <div style={{ color: '#6F8FA7' }}>{run.country || 'all'}</div>
              <div>
                <span style={{ padding: '2px 7px', borderRadius: 999, fontSize: '.62rem', fontWeight: 600,
                  background: run.status === 'done' ? '#eaf7f2' : run.status === 'running' ? '#e0f5fc' : '#fef0ef',
                  color:      run.status === 'done' ? '#0d6640' : run.status === 'running' ? '#185FA5' : '#8b1c1c' }}>
                  {run.status}
                </span>
              </div>
              <div style={{ color: '#9eb8c8' }}>
                {run.processed != null ? fmt(run.processed) + (run.total ? ' / ' + fmt(run.total) : '') : '\u2014'}
              </div>
              <div style={{ color: '#b0c8d4', fontSize: '.62rem' }}>
                {run.started_at ? new Date(run.started_at).toLocaleDateString('en-GB', { day:'2-digit', month:'short' }) : '\u2014'}
              </div>
            </div>
          ))}
          </div></div>
        </Card>
      )}
    </div>
  );
}
